import { QuestionModel } from "../models/QuestionModel";
import { QuestionType } from "../models/enums/QuestionType";

export function useAriaLabelledByForQuestionOpenEndChoiceBox(question: QuestionModel) {
    switch (question.type) {
        case QuestionType.RadioButton:
        case QuestionType.CheckBox:
        case QuestionType.DrillDown:
        case QuestionType.DropDownList:
        case QuestionType.TextAnswer:
        case QuestionType.EmailAddressAnswer:
        case QuestionType.NumericAnswer:
        case QuestionType.RespondentPhone:
        case QuestionType.OpenEndAnswer:
        // Grid question row/column too have openend inside cell
        // eslint-disable-next-line no-fallthrough
        case QuestionType.ChoiceGrid:
        case QuestionType.ImageChoiceGrid:
        case QuestionType.RunningTotal:
        case QuestionType.StarRatingGrid:
        case QuestionType.NetPromoter:
        case QuestionType.HybridGrid:
            return true;
        default:
            return false;
    }
}

export function useAriaLabelledByForInputFieldQuestion(question: QuestionModel) {
    const questionType = question?.parent?.type ?? question?.type;

    switch (questionType) {
        case QuestionType.TextAnswer:
        case QuestionType.EmailAddressAnswer:
        case QuestionType.NumericAnswer:
        case QuestionType.RespondentEmail:
        case QuestionType.RespondentPhone:
        case QuestionType.OpenEndAnswer:
        case QuestionType.ChoiceGrid:
        case QuestionType.ImageChoiceGrid:
        case QuestionType.HybridGrid: // For handling radio/checkbox/image choice rendering
            return true;
        default:
            return false;
    }
}

export function useAriaLabelByForGridRenderedInputFieldQuestion(question: QuestionModel) {
    const questionType = question?.parent?.type ?? question?.type;

    // Donot add Hybrid grid case here as it will break radio/checkbox/image choice rendering
    switch (questionType) {
        case QuestionType.RunningTotal:
            return false;
        default:
            return true;
    }
}

export function isFieldsetGroupingQuestion(question: QuestionModel) {
    switch (question.type) {
        case QuestionType.CheckBox:
        case QuestionType.RadioButton:
        case QuestionType.DrillDown:
        case QuestionType.NetPromoter:
            return true;
        default:
            return false;
    }
}

export function getAriaLabelledByFromId(
    question: QuestionModel,
    nameOrElementId: string,
    fieldWithinQuestionType?: string,
    gridRenderedColumnIndex?: number,
    gridRenderedRowIndex?: number,
    gridRenderedChoiceIndex?: number,
) {
    if (!nameOrElementId) {
        return "";
    }
    let questionType: any;

    if (fieldWithinQuestionType) {
        questionType = fieldWithinQuestionType;
    } else {
        questionType = question?.parent?.type ?? question?.type;
    }

    const leftLabelId = `${nameOrElementId}_ROW_${gridRenderedRowIndex}_LEFTLABEL`;

    // If same question have 2 field types use fieldWithinQuestionType case first
    switch (questionType) {
        case QuestionType.RadioButton:
        case QuestionType.CheckBox:
        case QuestionType.TextAnswer:
        case QuestionType.EmailAddressAnswer:
        case QuestionType.NumericAnswer:
        case QuestionType.RespondentEmail:
        case QuestionType.RespondentPhone:
        case QuestionType.OpenEndAnswer:
        case QuestionType.NetPromoter:
            return `${nameOrElementId}_LABEL`;
        case `${QuestionType.RunningTotal}_INPUTBOX`:
            return `${leftLabelId} ${nameOrElementId}_HEADER_${gridRenderedColumnIndex}`;
        case `${QuestionType.ChoiceGrid}_CHOICEOPTION`:
            return `${leftLabelId} ${nameOrElementId}_CHOICEHEADER_${gridRenderedChoiceIndex}`;
        case `${QuestionType.ImageChoiceGrid}_CHOICEOPTION`:
            return `${leftLabelId} ${nameOrElementId}_CHOICEHEADER_${gridRenderedChoiceIndex}`;
        case QuestionType.ChoiceGrid:
        case QuestionType.ImageChoiceGrid:
        case QuestionType.RunningTotal:
        case QuestionType.StarRatingGrid:
            return `${nameOrElementId}_CELL_LABEL`;
        case `${QuestionType.ChoiceGrid}_OPENENDROWHEADER`:
        case `${QuestionType.ImageChoiceGrid}_OPENENDROWHEADER`:
        case `${QuestionType.RunningTotal}_OPENENDROWHEADER`:
        case `${QuestionType.StarRatingGrid}_OPENENDROWHEADER`:
        case `${QuestionType.HybridGrid}_OPENENDROWHEADER`:
            return `${leftLabelId}`;
        case QuestionType.DrillDown:
        case QuestionType.DropDownList:
            return nameOrElementId;
        case `${QuestionType.HybridGrid}_INPUTBOX`:
            return `${leftLabelId} ${nameOrElementId}_HEADER_${gridRenderedColumnIndex}`;
        case `${QuestionType.HybridGrid}_CHOICEOPTION`:
            return `${leftLabelId} ${nameOrElementId}_HEADER_${gridRenderedColumnIndex} ${nameOrElementId}_MAINCOLHEADER${gridRenderedColumnIndex}_CHOICEHEADER_${gridRenderedChoiceIndex}`;
        case `${QuestionType.HybridGrid}_SLIDER`:
        case `${QuestionType.HybridGrid}_STARRATING`:
            return `${leftLabelId} ${nameOrElementId}_HEADER_${gridRenderedColumnIndex}`;
        default:
            return "";
    }
}

export function getRoleForQuestion(question: QuestionModel) {
    switch (question.type) {
        case QuestionType.RadioButton:
        case QuestionType.CheckBox:
            return undefined;
        // For below question types normal a text box is display but choices can also be displayed
        // and because it doesn't have any grouping i.e. radiogroup is used
        case QuestionType.EmailAddressAnswer:
        case QuestionType.TextAnswer:
        case QuestionType.NumericAnswer:
        case QuestionType.OpenEndAnswer:
            return "radiogroup";
        default:
            return "group";
    }
}
